import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DocumentText1, User, Edit } from "iconsax-react";
import { ModalForm, Button, Prompt, Toast, FileViewer } from "@/shared/ui";
import { SigningStatus } from "@/shared/components";
import { useOrderSigning, useCounterSignDocumentMutation } from "@/features/signing";
import type { SigningStatus as SigningStatusValue } from "@/shared/types";
import { runtimeConfig } from "@/shared/config/runtime";

interface Props {
  documentId: string;
  bytesUrl?: string;
  fileUrl?: string;
  workerName: string;
  documentType: string;
  signingStatus: SigningStatusValue;
  onClose: () => void;
  onSuccess: () => void;
}

export default function CounterSignDocumentModal({
  documentId,
  bytesUrl,
  fileUrl,
  workerName,
  documentType,
  signingStatus,
  onClose,
  onSuccess,
}: Props) {
  const { t } = useTranslation("PendingActionsPage");
  const { t: tCommon } = useTranslation("Common");

  const [prompt, setPrompt] = useState<{ title: string; text: string; variant: "success" | "error" } | null>(null);
  const [counterSign] = useCounterSignDocumentMutation();

  const dataUrl = bytesUrl
    ? `${runtimeConfig.apiUrl}${bytesUrl}`
    : `${runtimeConfig.apiUrl}/api/signing-documents/${documentId}/data/`;

  const signing = useOrderSigning({
    async previewFn() {
      const res = await fetch(dataUrl, { credentials: "include" });
      if (!res.ok) throw new Error(`Failed to fetch PDF: ${res.status} ${res.statusText}`);
      const ct = res.headers.get("content-type") || "";
      if (!ct.startsWith("application/pdf")) throw new Error(`Expected application/pdf, got ${ct}`);
      return res.blob();
    },
    signFn(cmsSignature) {
      return counterSign({ documentId, cms_signature: cmsSignature }).unwrap();
    },
    skipFileUpload: true,
    onSuccess() {
      setPrompt({
        title: t("counterSign.successTitle"),
        text: t("counterSign.successText"),
        variant: "success",
      });
    },
    setPrompt(p) {
      setPrompt(p);
    },
    t: tCommon,
  });

  return (
    <>
      {signing.isLoading && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={tCommon(`signing.phases.${signing.phase}`)}
          additionalText={tCommon("signing.phases.pleaseWait")}
          namespace="Common"
        />
      )}

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant}
          onClose={() => {
            const wasSuccess = prompt.variant === "success";
            setPrompt(null);
            if (wasSuccess) onSuccess();
          }}
          namespace="PendingActionsPage"
        />
      )}

      <ModalForm
        icon={DocumentText1}
        onClose={onClose}
        resize={false}
        footer={
          <div className="flex flex-col gap-3">
            <Toast color="grey" text={tCommon("signing.ncalayerHint")} closable={false} autoClose={false} isFullWidth />
            <Button
              variant="primary"
              size="huge"
              className="w-full"
              disabled={signing.isLoading}
              onClick={() => signing.handleSign()}>
              <Edit size={16} color="currentColor" />
              {t("counterSign.signButton")}
            </Button>
          </div>
        }>
        <div className="flex flex-col gap-6 h-full min-h-0">
          <div className="flex flex-col gap-2 pb-5 border-b surface-base-stroke shrink-0">
            <h4 className="text-display-2xs content-base-primary">{t("counterSign.title")}</h4>
            <p className="text-body-regular-md content-base-secondary">{t("counterSign.description")}</p>
          </div>

          <div className="flex-1 overflow-auto min-h-0 p-1">
            <div className="flex flex-col py-4">
              <div className="py-3 flex items-center gap-3 border-b surface-base-stroke">
                <span className="content-action-brand">
                  <User size={16} color="currentColor" />
                </span>
                <span className="text-body-regular-md content-base-secondary flex-1 min-w-[108px]">
                  {t("counterSign.workerName")}
                </span>
                <p className="text-body-bold-md content-base-primary text-right">{workerName}</p>
              </div>
              <div className="py-3 flex items-center gap-3 border-b surface-base-stroke">
                <span className="content-action-brand">
                  <DocumentText1 size={16} color="currentColor" />
                </span>
                <span className="text-body-regular-md content-base-secondary flex-1 min-w-[108px]">
                  {t("counterSign.documentType")}
                </span>
                <p className="text-body-bold-md content-base-primary text-right">{documentType}</p>
              </div>
              <div className="py-3 flex items-center gap-3 border-b surface-base-stroke">
                <span className="content-action-brand">
                  <DocumentText1 size={16} color="currentColor" />
                </span>
                <span className="text-body-regular-md content-base-secondary flex-1 min-w-[108px]">
                  {t("counterSign.statusLabel")}
                </span>
                <div className="flex justify-end">
                  <SigningStatus signingStatus={signingStatus} />
                </div>
              </div>
              <div className="py-3 flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <span className="content-action-brand">
                    <DocumentText1 size={16} color="currentColor" />
                  </span>
                  <span className="text-body-regular-md content-base-secondary">
                    {t("counterSign.documentLabel")}
                  </span>
                </div>
                <FileViewer existingFileUrl={fileUrl ?? dataUrl} customFileName={fileUrl ? undefined : documentType} />
              </div>
            </div>
          </div>

        </div>
      </ModalForm>
    </>
  );
}
